import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Truck, Star, ChevronDown, Zap } from "lucide-react";

const MaskedLine = ({ children, i, className = "" }) => (
    <span className={`block overflow-hidden ${className}`}>
        <motion.span
            className="block"
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ delay: 0.25 + i * 0.14, duration: 1, ease: [0.16, 1, 0.3, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

export default function Hero({ onBuy }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
    const ghostY = useTransform(scrollYProgress, [0, 1], [0, -80]);

    return (
        <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden">
            <div className="spotlight absolute inset-0" />
            <motion.p
                style={{ y: ghostY }}
                aria-hidden
                className="font-display text-stroke absolute -bottom-6 left-0 text-[26vw] leading-none font-700 uppercase whitespace-nowrap select-none pointer-events-none"
            >
                AIRFLOW
            </motion.p>

            <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-28 md:pt-32 pb-16 grid lg:grid-cols-12 gap-12 items-center min-h-[100svh]">
                <div className="lg:col-span-7 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1, duration: 0.7 }}
                        className="inline-flex items-center gap-2 rounded-full border border-cyan-300/30 bg-cyan-300/10 px-4 py-1.5 mb-8"
                        data-testid="hero-eyebrow"
                    >
                        <Zap className="w-3.5 h-3.5 text-cyan-300" />
                        <span className="text-[11px] uppercase tracking-[0.25em] text-cyan-200">IcePlate™ — 3s Instant Cooling</span>
                    </motion.div>

                    <h1 className="font-display uppercase font-700 tracking-tighter text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95]" data-testid="hero-headline">
                        <MaskedLine i={0}>Ice-cold air.</MaskedLine>
                        <MaskedLine i={1}>
                            <span className="text-cyan-300">Anywhere</span> you
                        </MaskedLine>
                        <MaskedLine i={2}>go.</MaskedLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.85, duration: 0.8 }}
                        className="mt-6 max-w-md text-base md:text-lg text-zinc-400 leading-relaxed"
                        data-testid="hero-subcopy"
                    >
                        A pocket-sized fan with a chilling cold plate, 9m/s airflow and a 12-hour battery. Your personal breeze for the desk, the commute and everywhere in between.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1, duration: 0.8 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <button
                            onClick={onBuy}
                            data-testid="hero-buy-button"
                            className="glow-btn rounded-full bg-cyan-300 text-black font-bold uppercase tracking-[0.15em] text-sm px-9 py-4 transition-transform duration-300 hover:scale-105 active:scale-95"
                        >
                            Buy Now — $18.99
                        </button>
                        <a
                            href="#cooling"
                            data-testid="hero-demo-link"
                            className="rounded-full border border-white/15 text-sm uppercase tracking-[0.15em] px-8 py-4 text-zinc-300 hover:border-cyan-300/60 hover:text-white transition-colors duration-300"
                        >
                            See the tech
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.2, duration: 0.8 }}
                        className="mt-12 flex flex-wrap gap-x-8 gap-y-3"
                        data-testid="hero-trust-badges"
                    >
                        {[
                            { icon: ShieldCheck, label: "Secure checkout" },
                            { icon: Truck, label: "Ships in 24–48h" },
                            { icon: Star, label: "Loved by first customers" },
                        ].map(({ icon: Icon, label }) => (
                            <span key={label} className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-[0.15em]">
                                <Icon className="w-4 h-4 text-cyan-300/80" /> {label}
                            </span>
                        ))}
                    </motion.div>
                </div>

                <div className="lg:col-span-5 relative z-10">
                    <motion.div style={{ y: imgY }} className="relative">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.92, rotate: 3 }}
                            animate={{ opacity: 1, scale: 1, rotate: 0 }}
                            transition={{ delay: 0.5, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                            className="animate-float"
                        >
                            <div className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-[0_40px_120px_-20px_rgba(0,229,255,0.25)]">
                                <img
                                    src="/images/fan-white-fridge.jpeg"
                                    alt="AEROCHILL TurboFan Pro — Glacier White with IcePlate cooling"
                                    className="w-full h-auto object-cover"
                                    data-testid="hero-product-image"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                            </div>
                            <div className="absolute -bottom-5 -left-5 rounded-2xl backdrop-blur-2xl bg-black/60 border border-white/10 px-5 py-3.5" data-testid="hero-airflow-chip">
                                <p className="font-display text-2xl text-cyan-300 leading-none">9 m/s</p>
                                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-1">Strong airflow</p>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>

            <motion.a
                href="#shop"
                data-testid="hero-scroll-cue"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6 }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-zinc-500 hover:text-cyan-300 transition-colors"
            >
                <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
                <ChevronDown className="w-4 h-4 animate-scroll-pulse" />
            </motion.a>
        </section>
    );
}
